import React, { useMemo, useState } from "react";
import { Col, Grid } from "@tremor/react";
import { useTranslation } from "react-i18next";

import { ChatAlerts } from "~/modules/Dashboard/AppStats/ChatAlerts";
import {
  type ComparisonMode,
  DEFAULT_COMPARISON_MODE,
  formatRangeLabel,
  getComparisonRange,
  resolveRange,
} from "~/modules/Dashboard/AppStats/hooks/comparisonRange";
import { type DateRange } from "~/modules/Dashboard/AppStats/hooks/types";
import { ALL_PRODUCTS_ITEM_ID } from "~/modules/Dashboard/AppStats/hooks/useProductsList";
import { NewClientsChart } from "~/modules/Dashboard/AppStats/newClients/NewClientsChart";
import { useNewClientsStats } from "~/modules/Dashboard/AppStats/newClients/useNewClientsStats";
import { SalesFunnelCard } from "~/modules/Dashboard/AppStats/salesFunnel/SalesFunnelCard";
import { useSalesFunnelStats } from "~/modules/Dashboard/AppStats/salesFunnel/useSalesFunnelStats";
import { SalesSummary } from "~/modules/Dashboard/AppStats/SalesSummary";
import { DEFAULT_STATS_RANGE, StatsFilters } from "~/modules/Dashboard/AppStats/StatsFilters";

export const AppStats = (): React.ReactElement => {
  const { t } = useTranslation("dashboard");

  const [products, setProducts] = useState<string[]>([ALL_PRODUCTS_ITEM_ID]);
  const [range, setRange] = useState<DateRange>(DEFAULT_STATS_RANGE);
  const [comparisonMode, setComparisonMode] = useState<ComparisonMode>(DEFAULT_COMPARISON_MODE);

  const handleProductsChange = (value: string[]) => {
    // "All products" is a shortcut, not a product: it gives way to the first real selection.
    setProducts(value.length > 1 ? value.filter((item) => item !== ALL_PRODUCTS_ITEM_ID) : value);
  };

  const resolvedRange = useMemo(() => resolveRange(range), [range]);
  const comparisonRange = useMemo(() => getComparisonRange(range, comparisonMode), [range, comparisonMode]);
  // The compared dates are spelled out so a month-end sales push reads as "28–31.08: 204 510 zł",
  // not as a collapse of the new month.
  const comparisonLabel = comparisonRange ? formatRangeLabel(comparisonRange) : t("stats.previousPeriod");

  const newClients = useNewClientsStats(products, resolvedRange, comparisonRange);
  // The funnel is not per product: a lead has no product yet and a call is booked before one is picked.
  const salesFunnel = useSalesFunnelStats(resolvedRange, comparisonRange);

  return (
    <main className="flex flex-col gap-6">
      <StatsFilters
        products={products}
        onProductsChange={handleProductsChange}
        range={range}
        onRangeChange={setRange}
        comparisonMode={comparisonMode}
        onComparisonModeChange={setComparisonMode}
      />

      <SalesSummary
        products={products}
        range={range}
        comparisonRange={comparisonRange}
        comparisonLabel={comparisonLabel}
      />

      <Grid numItemsLg={6} className="stats gap-6">
        <Col numColSpanLg={4}>
          <NewClientsChart
            current={newClients.current}
            previous={newClients.previous}
            comparisonLabel={comparisonLabel}
            isLoading={newClients.isLoading}
          />
        </Col>

        <Col numColSpanLg={2}>
          <SalesFunnelCard
            current={salesFunnel.current}
            previous={salesFunnel.previous}
            comparisonLabel={comparisonLabel}
            isLoading={salesFunnel.isLoading}
          />
        </Col>
      </Grid>

      <ChatAlerts />
    </main>
  );
};
